import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';
import { Clock, FileText, ShieldCheck, ArrowRight, ChevronRight, Star, Users, CheckCircle, Crown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CookieConsent } from '@/components/CookieConsent';

const HERO_IMAGE = "https://images.unsplash.com/photo-1672266617259-4fd968406d27?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxwYXJlbnQlMjBjaGlsZCUyMHBsYXlpbmclMjBpbmRvb3IlMjB3YXJtJTIwbGlnaHQlMjBmYW1pbHl8ZW58MHx8fHwxNzc2MTczNzc5fDA&ixlib=rb-4.1.0&q=85";

const features = [
  {
    icon: Clock,
    title: 'Suivi des heures',
    description: 'Pointez et validez les heures de garde en temps réel. Finis les carnets et les oublis.',
    color: 'var(--primary-400)',
    bgColor: 'var(--bg-tertiary)',
  },
  {
    icon: FileText,
    title: 'Gestion des contrats',
    description: 'Créez et gérez vos contrats d\'accueil en quelques clics. Tout est centralisé.',
    color: 'var(--secondary-400)',
    bgColor: 'var(--secondary-50)',
  },
  {
    icon: ShieldCheck,
    title: 'Simplification administrative',
    description: 'Bulletins de paie, déclarations Pajemploi — automatisés et conformes.',
    color: 'var(--primary-600)',
    bgColor: 'var(--primary-50)',
  },
];

const stats = [
  { value: '100%', label: 'Conforme Urssaf' },
  { value: '2 min', label: 'Pour pointer' },
  { value: '0', label: 'Papier nécessaire' },
];

const PricingFeature = ({ text, highlight = false }) => (
  <div className="flex items-start gap-2">
    <CheckCircle 
      className="h-4 w-4 mt-0.5 shrink-0" 
      style={{ color: highlight ? 'var(--primary-400)' : 'var(--text-tertiary)' }} 
    />
    <span 
      className={`text-sm ${highlight ? 'font-medium' : ''}`}
      style={{ color: highlight ? 'var(--text-primary)' : 'var(--text-secondary)' }}
    >
      {text}
    </span>
  </div>
);

const Landing = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [stripeProducts, setStripeProducts] = useState(null);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/accueil', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  // Fetch pricing from Stripe (dynamic) and fallback config
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const axios = await import('axios').then(m => m.default);
        const API = process.env.REACT_APP_BACKEND_URL;
        
        // Fetch both Stripe products and hardcoded config
        const [stripeRes, configRes] = await Promise.all([
          axios.get(`${API}/api/payments/stripe-products`).catch(() => null),
          axios.get(`${API}/api/payments/pricing`).catch(() => null),
        ]);
        
        if (stripeRes?.data) {
          setStripeProducts(stripeRes.data);
        }
        if (configRes?.data) {
          setPricing(configRes.data);
        }
      } catch (err) {
        console.warn('Landing: failed to fetch pricing', err);
      }
    };
    fetchPricing();
  }, []);

  // Helper to get display price from Stripe or fallback
  const getDisplayPrice = (planKey, period) => {
    // Try Stripe first
    if (stripeProducts && stripeProducts[planKey]) {
      const stripeProduct = stripeProducts[planKey];
      const stripePrice = stripeProduct.prices?.[period];
      if (stripePrice) {
        return stripePrice.amount_display + '€';
      }
    }
    // Fallback to hardcoded config
    if (pricing && pricing[planKey] && pricing[planKey][period]) {
      return pricing[planKey][period].display_price;
    }
    // Ultimate fallback
    const fallbacks = {
      parent_pro: { monthly: '4,99€', annual: '49€' },
      nanny_pro: { monthly: '6,99€', annual: '69€' }
    };
    return fallbacks[planKey]?.[period] || '…';
  };

  // Helper to get display name from Stripe or fallback
  const getDisplayPlanName = (planKey) => {
    // Try Stripe first
    if (stripeProducts && stripeProducts[planKey]) {
      return stripeProducts[planKey].name;
    }
    // Fallback to hardcoded config
    if (pricing && pricing[planKey]) {
      return pricing[planKey].plan_name;
    }
    // Ultimate fallback
    return planKey === 'parent_pro' ? 'Parent Pro' : 'Nounou Pro';
  };

  if (loading) return null;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="landing-page">
      {/* ═══════════ HEADER ═══════════ */}
      <header 
        className="sticky top-0 z-50 w-full border-b backdrop-blur-md"
        style={{ 
          backgroundColor: 'var(--bg-primary-translucent)', 
          borderColor: 'var(--neutral-200)' 
        }}
        data-testid="landing-header"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 h-16 py-2" data-testid="landing-logo">
            <img 
              src="/assets/logo.png" 
              alt="Nannyou" 
              className="h-full object-contain"
            />
            {(() => {
              const env = (process.env.REACT_APP_ENV || '').toLowerCase();
              if (env === 'recette') {
                return (
                  <span 
                    className="text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded ml-2"
                    style={{ 
                      color: '#FFFFFF',
                      backgroundColor: '#DC2626',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    R7
                  </span>
                );
              } else if (env === 'preprod') {
                  return (
                    <span 
                      className="text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded"
                      style={{ 
                        color: '#FFFFFF',
                        backgroundColor: '#F59E0B',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      PPR
                    </span>
                  );
                } else if (['dev', 'development', 'staging'].includes(env)) {
                  return (
                    <span 
                      className="text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded"
                      style={{ 
                        color: '#FFFFFF',
                        backgroundColor: '#DC2626',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      DEV
                    </span>
                  );
                }
                return null;
              })()}
          </Link>

          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="header-login-btn">
              <Button 
                variant="ghost" 
                className="text-sm font-medium hidden sm:inline-flex"
                style={{ color: 'var(--text-secondary)' }}
              >
                Se connecter
              </Button>
            </Link>
            <Link to="/onboarding" data-testid="header-register-btn">
              <Button 
                className="text-sm font-semibold"
                style={{ 
                  backgroundColor: 'var(--primary-400)', 
                  color: 'white',
                }}
              >
                Commencer gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* Subtle background pattern */}
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            background: `radial-gradient(circle at 20% 50%, var(--bg-tertiary) 0%, transparent 50%),
                         radial-gradient(circle at 80% 20%, var(--secondary-50) 0%, transparent 40%)`,
          }}
        />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Text content */}
            <div className="space-y-8 text-center lg:text-left">
              <div className="space-y-4">
                <div 
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{ 
                    backgroundColor: 'var(--bg-tertiary)', 
                    color: 'var(--primary-600)' 
                  }}
                >
                  <Star className="h-3.5 w-3.5" />
                  Gratuit pour commencer
                </div>
                
                <h1 
                  className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-tight tracking-tight"
                  style={{ fontFamily: 'var(--font-secondary)', color: 'var(--neutral-800)' }}
                >
                  La relation parent ↔ nounou,{' '}
                  <span style={{ color: 'var(--primary-400)' }}>simplifiée</span>
                </h1>
                
                <p 
                  className="text-lg sm:text-xl max-w-lg mx-auto lg:mx-0 leading-relaxed"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Pointage, contrats, paie et déclarations Pajemploi — tout en un. 
                  Un seul abonnement suffit pour les deux.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link to="/onboarding" data-testid="hero-register-btn">
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto text-base font-semibold px-8 h-12 gap-2 transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]"
                    style={{ backgroundColor: 'var(--primary-400)', color: 'white' }}
                  >
                    Commencer gratuitement
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/login" data-testid="hero-login-btn">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="w-full sm:w-auto text-base font-medium px-8 h-12 gap-2"
                    style={{ 
                      borderColor: 'var(--neutral-200)', 
                      color: 'var(--text-primary)' 
                    }}
                  >
                    Se connecter
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center gap-6 justify-center lg:justify-start pt-2">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p 
                      className="text-xl font-bold" 
                      style={{ fontFamily: 'var(--font-mono)', color: 'var(--primary-500)' }}
                    >
                      {stat.value}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero image */}
            <div className="relative hidden lg:block">
              <div 
                className="relative rounded-2xl overflow-hidden shadow-2xl"
                style={{ aspectRatio: '4/3' }}
              >
                <img 
                  src={HERO_IMAGE}
                  alt="Parent et enfant — la confiance au coeur de Nannyou"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                {/* Soft overlay to blend with theme */}
                <div 
                  className="absolute inset-0"
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(230,244,245,0.15) 0%, transparent 60%)' 
                  }}
                />
              </div>
              {/* Floating card accent */}
              <div 
                className="absolute -bottom-4 -left-4 p-4 rounded-xl shadow-lg border"
                style={{ 
                  backgroundColor: 'var(--bg-primary)', 
                  borderColor: 'var(--neutral-200)' 
                }}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="h-10 w-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--bg-tertiary)' }}
                  >
                    <CheckCircle className="h-5 w-5" style={{ color: 'var(--primary-400)' }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      Heures validées
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                      Avril 2026 — 142h
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ FEATURES ═══════════ */}
      <section 
        className="py-16 sm:py-20 lg:py-24" 
        style={{ backgroundColor: 'var(--neutral-50)' }}
        data-testid="features-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold tracking-tight"
              style={{ fontFamily: 'var(--font-secondary)', color: 'var(--neutral-800)' }}
            >
              Tout ce qu'il vous faut,{' '}
              <span style={{ color: 'var(--primary-400)' }}>rien de plus</span>
            </h2>
            <p 
              className="mt-4 text-base sm:text-lg leading-relaxed"
              style={{ color: 'var(--text-secondary)' }}
            >
              Conçu pour les parents employeurs et les assistantes maternelles. 
              Simple, rapide, efficace.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={feature.title}
                  className="group relative p-6 sm:p-8 rounded-2xl border transition-all duration-200 hover:shadow-lg hover:-translate-y-1"
                  style={{ 
                    backgroundColor: 'var(--bg-primary)', 
                    borderColor: 'var(--neutral-200)' 
                  }}
                  data-testid={`feature-card-${feature.title}`}
                >
                  <div 
                    className="h-12 w-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: feature.bgColor }}
                  >
                    <Icon className="h-6 w-6" style={{ color: feature.color }} />
                  </div>
                  
                  <h3 
                    className="text-xl font-semibold mb-2"
                    style={{ fontFamily: 'var(--font-secondary)', color: 'var(--neutral-800)' }}
                  >
                    {feature.title}
                  </h3>
                  
                  <p 
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ HOW IT WORKS ═══════════ */}
      <section className="py-16 sm:py-20 lg:py-24" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold tracking-tight"
              style={{ fontFamily: 'var(--font-secondary)', color: 'var(--neutral-800)' }}
            >
              Comment ça marche ?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                step: '1',
                title: 'Créez votre compte',
                desc: 'Inscrivez-vous gratuitement en tant que parent. C\'est rapide et sécurisé.',
              },
              {
                step: '2',
                title: 'Ajoutez un contrat',
                desc: 'Renseignez les informations de votre enfant et invitez votre assistante maternelle.',
              },
              {
                step: '3',
                title: 'Gérez tout simplement',
                desc: 'Pointage des heures, validation, bulletins de paie et déclarations — en quelques clics.',
              },
            ].map((item) => (
              <div key={item.step} className="text-center space-y-4">
                <div 
                  className="mx-auto h-14 w-14 rounded-full flex items-center justify-center text-xl font-bold"
                  style={{ 
                    backgroundColor: 'var(--bg-tertiary)', 
                    color: 'var(--primary-500)',
                    fontFamily: 'var(--font-secondary)',
                  }}
                >
                  {item.step}
                </div>
                <h3 
                  className="text-lg font-semibold"
                  style={{ fontFamily: 'var(--font-secondary)', color: 'var(--neutral-800)' }}
                >
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ PRICING ═══════════ */}
      <section 
        className="py-16 sm:py-24"
        style={{ backgroundColor: 'var(--bg-secondary)' }}
        data-testid="pricing-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 space-y-3">
            <div 
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: 'var(--primary-50)', color: 'var(--primary-600)' }}
            >
              <Crown className="h-3.5 w-3.5" />
              Un seul abonnement suffit
            </div>
            <h2 
              className="text-2xl sm:text-3xl font-bold tracking-tight"
              style={{ fontFamily: 'var(--font-secondary)', color: 'var(--neutral-800)' }}
            >
              Des prix simples et transparents
            </h2>
            <p className="text-base max-w-lg mx-auto" style={{ color: 'var(--text-secondary)' }}>
              Commencez gratuitement, passez à la version complète quand vous en avez besoin. Un abonnement débloque les fonctionnalités pour les deux.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Free */}
            <Card 
              className="relative border transition-all duration-200 hover:shadow-md"
              style={{ borderColor: 'var(--neutral-200)', backgroundColor: 'var(--bg-primary)' }}
              data-testid="pricing-free"
            >
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-lg" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>
                  Gratuit
                </CardTitle>
                <div className="mt-3">
                  <span className="text-3xl font-bold" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>0€</span>
                  <span className="text-sm ml-1" style={{ color: 'var(--text-tertiary)' }}>/mois</span>
                </div>
                <CardDescription className="mt-2">Pour découvrir l'application</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-4">
                <PricingFeature text="1 enfant (1 contrat)" />
                <PricingFeature text="Pointage des heures" />
                <PricingFeature text="Validation parent" />
                <PricingFeature text="Historique 1 mois" />
                <div className="pt-4">
                  <Link to="/onboarding">
                    <Button 
                      variant="outline" 
                      className="w-full h-11 font-medium transition-transform duration-200 hover:scale-[1.01]"
                      style={{ borderColor: 'var(--neutral-300)', color: 'var(--text-primary)' }}
                      data-testid="pricing-free-cta"
                    >
                      Commencer gratuitement
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Parent Pro */}
            <Card 
              className="relative border-2 transition-all duration-200 hover:shadow-lg shadow-md"
              style={{ borderColor: 'var(--primary-400)', backgroundColor: 'var(--bg-primary)' }}
              data-testid="pricing-parent-pro"
            >
              <div 
                className="absolute -top-3 left-1/2 -translate-x-1/2"
              >
                <Badge 
                  className="text-xs font-semibold px-3 py-1"
                  style={{ backgroundColor: 'var(--primary-400)', color: 'white' }}
                >
                  <Star className="h-3 w-3 mr-1" />
                  Populaire
                </Badge>
              </div>
              <CardHeader className="text-center pb-2 pt-6">
                <CardTitle className="text-lg" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--primary-500)' }}>
                  {getDisplayPlanName('parent_pro')}
                </CardTitle>
                <div className="mt-3">
                  <span className="text-3xl font-bold" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>
                    {getDisplayPrice('parent_pro', 'monthly')}
                  </span>
                  <span className="text-sm ml-1" style={{ color: 'var(--text-tertiary)' }}>/mois</span>
                </div>
                <CardDescription className="mt-2">ou {getDisplayPrice('parent_pro', 'annual')}/an <span className="text-xs" style={{ color: 'var(--primary-500)' }}>(-18%)</span></CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-4">
                <PricingFeature text="Enfants illimités" highlight />
                <PricingFeature text="Bulletins de paie PDF" highlight />
                <PricingFeature text="Déclarations Pajemploi" highlight />
                <PricingFeature text="Historique complet" highlight />
                <PricingFeature text="Profite aussi à votre nounou" highlight />
                <div className="pt-4">
                  <Link to="/onboarding">
                    <Button 
                      className="w-full h-11 font-semibold transition-transform duration-200 hover:scale-[1.01]"
                      style={{ backgroundColor: 'var(--primary-400)', color: 'white' }}
                      data-testid="pricing-parent-cta"
                    >
                      Essayer gratuitement
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Nounou Pro */}
            <Card 
              className="relative border transition-all duration-200 hover:shadow-md"
              style={{ borderColor: 'var(--secondary-300)', backgroundColor: 'var(--bg-primary)' }}
              data-testid="pricing-nanny-pro"
            >
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-lg" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--secondary-500)' }}>
                  {getDisplayPlanName('nanny_pro')}
                </CardTitle>
                <div className="mt-3">
                  <span className="text-3xl font-bold" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>
                    {getDisplayPrice('nanny_pro', 'monthly')}
                  </span>
                  <span className="text-sm ml-1" style={{ color: 'var(--text-tertiary)' }}>/mois</span>
                </div>
                <CardDescription className="mt-2">ou {getDisplayPrice('nanny_pro', 'annual')}/an <span className="text-xs" style={{ color: 'var(--secondary-500)' }}>(-18%)</span></CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-4">
                <PricingFeature text="Toutes les familles" highlight />
                <PricingFeature text="Bulletins de paie PDF" highlight />
                <PricingFeature text="Déclarations Pajemploi" highlight />
                <PricingFeature text="Historique complet" highlight />
                <PricingFeature text="Profite aussi à vos employeurs" highlight />
                <div className="pt-4">
                  <Link to="/onboarding">
                    <Button 
                      variant="outline"
                      className="w-full h-11 font-medium transition-transform duration-200 hover:scale-[1.01]"
                      style={{ borderColor: 'var(--secondary-400)', color: 'var(--secondary-600)' }}
                      data-testid="pricing-nanny-cta"
                    >
                      Essayer gratuitement
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-sm mt-8" style={{ color: 'var(--text-tertiary)' }}>
            <Sparkles className="inline h-3.5 w-3.5 mr-1" style={{ color: 'var(--secondary-400)' }} />
            14 jours d'essai gratuit — aucun engagement — annulable à tout moment
          </p>
        </div>
      </section>

      {/* ═══════════ CTA ═══════════ */}
      <section 
        className="py-16 sm:py-20"
        data-testid="cta-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="relative rounded-2xl overflow-hidden px-6 py-12 sm:px-12 sm:py-16 text-center"
            style={{ backgroundColor: 'var(--bg-tertiary)' }}
          >
            {/* Subtle pattern */}
            <div 
              className="absolute inset-0 opacity-30"
              style={{
                background: 'radial-gradient(circle at 30% 70%, var(--primary-100) 0%, transparent 50%)',
              }}
            />
            
            <div className="relative z-10 max-w-xl mx-auto space-y-6">
              <div 
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--primary-600)' }}
              >
                <Users className="h-3.5 w-3.5" />
                Rejoignez Nannyou
              </div>
              
              <h2 
                className="text-2xl sm:text-3xl font-bold tracking-tight"
                style={{ fontFamily: 'var(--font-secondary)', color: 'var(--neutral-800)' }}
              >
                Simplifiez votre quotidien dès aujourd'hui
              </h2>
              
              <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
                Gagnez du temps sur l'administratif et concentrez-vous sur l'essentiel. Un seul abonnement suffit pour débloquer toutes les fonctionnalités.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Link to="/onboarding" data-testid="cta-register-btn">
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto text-base font-semibold px-8 h-12 gap-2 transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]"
                    style={{ backgroundColor: 'var(--primary-400)', color: 'white' }}
                  >
                    Créer mon compte gratuit
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/login" data-testid="cta-login-btn">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="w-full sm:w-auto text-base font-medium px-8 h-12"
                    style={{ 
                      backgroundColor: 'var(--bg-primary)',
                      borderColor: 'var(--neutral-200)', 
                      color: 'var(--text-primary)' 
                    }}
                  >
                    Accéder à mon compte
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ FOOTER ═══════════ */}
      <footer 
        className="border-t py-8"
        style={{ borderColor: 'var(--neutral-200)' }}
        data-testid="landing-footer"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <img 
                src="/assets/logo.png" 
                alt="Nannyou" 
                className="h-10 object-contain"
              />
              {(() => {
                const env = (process.env.REACT_APP_ENV || '').toLowerCase();
                if (env === 'recette') {
                  return (
                    <span 
                      className="text-[9px] font-semibold tracking-wider px-1 py-0.5 rounded"
                      style={{ 
                        color: '#FFFFFF',
                        backgroundColor: '#DC2626',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      R7
                    </span>
                  );
                } else if (env === 'preprod') {
                    return (
                      <span 
                        className="text-[9px] font-semibold tracking-wider px-1 py-0.5 rounded"
                        style={{ 
                          color: '#FFFFFF',
                          backgroundColor: '#F59E0B',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        PPR
                      </span>
                    );
                  } else if (['dev', 'development', 'staging'].includes(env)) {
                    return (
                      <span 
                        className="text-[9px] font-semibold tracking-wider px-1 py-0.5 rounded"
                        style={{ 
                          color: '#FFFFFF',
                          backgroundColor: '#DC2626',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        DEV
                      </span>
                    );
                  }
                  return null;
                })()}
            </div>
            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
              &copy; {new Date().getFullYear()}
              </span>
            </div>
            
            <div className="flex items-center gap-6">
              <Link 
                to="/legal" 
                className="text-sm transition-colors duration-200 hover:underline"
                style={{ color: 'var(--text-secondary)' }}
                data-testid="footer-legal-link"
              >
                Mentions légales
              </Link>
              <a 
                href="mailto:contact@nannyou.fr" 
                className="text-sm transition-colors duration-200 hover:underline"
                style={{ color: 'var(--text-secondary)' }}
                data-testid="footer-contact-link"
              >
                Contact
              </a>
              <Link 
                to="/login" 
                className="text-sm transition-colors duration-200 hover:underline"
                style={{ color: 'var(--primary-400)' }}
                data-testid="footer-login-link"
              >
                Connexion
              </Link>
            </div>
          </div>
        </div>
      </footer>
      <CookieConsent />
    </div>
  );
};

export default Landing;
